import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Link,
  createFileRoute,
  useLocation,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { cardAddressSchema, cardCommentNumberPath, type CardAddress } from '@molt/shared';
import { BoardFace } from '@/components/board/BoardFace';
import {
  BoardError,
  BoardLoading,
  BoardSignInPrompt,
} from '@/components/board/BoardStates';
import { AvatarLabelsProvider, CardBack } from '@/components';
import { EmptyState } from '@/components/EmptyState';
import { get } from '@/lib/api';
import { boardAvatarRoster } from '@/lib/avatar-labels';
import { moveCardReturnToBoard, readCardOpenReturn } from '@/lib/board-card-return';
import {
  boardFallbackFor,
  cardBoardPath,
  isCanonicalCardPath,
} from '@/lib/board-address';
import { boardContentsQueryOptions, canEdit, type BoardViewer } from '@/lib/board-face';
import {
  boardFilterFromSearch,
  setBoardFilterLabels,
  type BoardFilterSelection,
  withBoardFilterSearchParams,
} from '@/lib/board-filter';
import { ownBoardRole, useBoardMembers } from '@/lib/boards';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { useBoardLive } from '@/lib/live-board';
import { queryKeys } from '@/lib/query-keys';
import { isSuperadminSession, useSession } from '@/lib/session';

/**
 * The card address — `FE-08.4`, chunk `D3`, agent J. `FR-EXP-015`,
 * `FR-CARD-005`.
 *
 * ---------------------------------------------------------------------------
 * ONE FILE, TWO URLS — `/c/<segment>/<slug>` AND `/c/<segment>`
 * (the segment is a short link or a `KEY-NNNN` reference,
 * `docs/notes/card-keys.md`; the param keeps its old name)
 * ---------------------------------------------------------------------------
 * Exactly the shape `/b/$shortLink/{-$slug}` uses, and for the same reason:
 * `FR-EXP-015` AC4 — "given a card short link, when it is used without the
 * slug, then it resolves identically" — and `GET /cards/by-short-link/:shortLink`
 * looks up by the short link alone. The optional segment is measured to match
 * all three of `/c/x/1-slug`, `/c/x` and `/c/x/` in **this file's own test**
 * (`c.$shortLink.{-$slug}.test.tsx`, "the card URL"), against the generated
 * route tree — the same measurement `lib/board-face.test.ts` makes for `/b/`,
 * made again here rather than borrowed, because that file covers the board
 * route and would go on passing if this one stopped matching.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THIS ROUTE RENDERS NO CARD, AND PRETENDS TO RENDER NONE
 * ---------------------------------------------------------------------------
 * `FR-EXP-015` AC2 — "the card detail is shown with its board visible behind
 * it, and closing the card leaves the user on that board" — is **`FE-14.1`'s,
 * chunk `E1`**. There is no card back in this product yet, and a route that
 * invented one would be building `E1`'s item.
 *
 * What D3 closes here is the **addressing**: the short link resolves, and the
 * visitor lands on the card's board. When `E1` lands it opens the card *over*
 * that board from this file — one call, with the board already resolved.
 *
 * ⚠ **Two more criteria wait on `FE-14.1` with AC2, and they look closed from
 * here.** `FR-EXP-015` AC1 and `FR-CARD-005` AC2 both say a stale card link
 * "resolves and the address is corrected to the new slug". D3 corrects nothing
 * *card-shaped*: it replaces the card address with the **board's** canonical
 * address, so there is no `/c/…` left in the bar for a corrected slug to appear
 * in. The card half of the resolution — `address.path`, which the server
 * already assembles from `cardPath()` — is consumed by nobody in this chunk and
 * is the value `E1` will put back in the bar once a card can be shown over its
 * board. Recorded here rather than left to read as met.
 *
 * ⚠ `cardAddressSchema.board` is the full board view, not an id, precisely so
 * this hop costs no second request. `cardBoardPath` (`lib/board-address.ts`)
 * goes through `packages/shared`'s own `boardPath` — the address is never
 * rebuilt from `shortLink` and `slug` here, and **never taken from
 * `address.path`**, which is the *card's* path: navigating there re-enters this
 * route, forever.
 *
 * ---------------------------------------------------------------------------
 * `navigate({ href })`, `replace: true`, AND THE SEARCH STRING
 * ---------------------------------------------------------------------------
 * `href`, not `to`: the destination is a computed same-origin path that may
 * carry a query string, and `to` is matched against the route-path union with
 * the querystring unparsed and dropped. `navigate({ href })` parses the string
 * into `to` + `search` + `hash` before building the location — `sign-in.tsx`
 * carries the measurement and the router-core reference.
 *
 * ⚠ **`replace: true`, never a push.** A corrected address that adds a history
 * entry turns Back into a redirect loop: Back lands on `/c/…`, which resolves
 * and forwards again. The test walks Back and asserts where it lands, because
 * a push and a replace leave the same `location.pathname` behind them.
 *
 * ⚠ **The search string is preserved by this file.** `lib/board-address.ts`
 * deliberately works on bare pathnames and says so — so `location.searchStr`
 * (which carries its own leading `?`, or is empty) is concatenated here.
 * `?filter=overdue` on a pasted card link survives the hop to the board.
 *
 * The hash is preserved. B61 comment/activity permalinks carry their target in
 * `#comment-…` / `#activity-…`; the card route resolves and canonicalises the
 * card path, then the mounted card back consumes the anchor for scroll and
 * highlight.
 *
 * ---------------------------------------------------------------------------
 * THE FALLBACK, AND WHY IT HAS A THIRD BRANCH `D3-BRIEF.md` §5.3 DOES NOT LIST
 * ---------------------------------------------------------------------------
 * A 404 takes the same session-aware fallback as the board route
 * (`boardFallbackFor`, §5.3): a signed-out visitor is prompted to sign in, a
 * signed-in one is told the card is not here. "No such card" and "a card on a
 * board you may not see" are the same 404 by design — the resolver inner-joins
 * `boards`, so the board's read scope applies through the join and the route is
 * not an existence oracle. `isSignedOut` is `isUnauthenticated(sessionError)`,
 * never `!profile`: an expired session keeps its last-good profile beside the
 * error.
 *
 * ⚠ §5.3's table has two rows; this file has three branches, and the extra one
 * is not a mistake. **On a pasted card link the session probe and the resolver
 * are in flight together**, and the resolver's 404 routinely lands first. At
 * that instant `session.error` is still `null`, so `isSignedOut` is `false` and
 * the table's signed-in row would tell a genuinely signed-out visitor the card
 * is gone — for a frame, then flipping to the sign-in prompt. That is `D12`'s
 * "we do not know yet must not render either branch", and on this route the
 * race is the normal path rather than an edge case, because nothing here is
 * ever warm. So while `session.isPending`, the loading state holds.
 *
 * ---------------------------------------------------------------------------
 * WHY THE ENDPOINT IS SPELLED HERE AND NOT IN `lib/`
 * ---------------------------------------------------------------------------
 * `lib/api.ts`'s own header: "the path is the block's to spell — this file does
 * not enumerate routes". There is exactly one caller of this resolver today and
 * it is the component below, so a `lib/card-address.ts` would be the module
 * `query-keys.ts` argues against shipping before its second caller (see its
 * note on the absent `workspaces.detail`). Both functions are exported so
 * `FE-14.1` can call them from the card back, and lift them into `lib/cards.ts`
 * when it does.
 */

/** `GET /cards/by-short-link/:shortLink` — `FR-EXP-015`, `FR-CARD-005`. */
export function getCardByShortLink(shortLink: string): Promise<CardAddress> {
  return get(`/cards/by-short-link/${encodeURIComponent(shortLink)}`, cardAddressSchema);
}

/**
 * The resolver query. Keyed by `queryKeys.cards.byShortLink`, which agent J
 * added to the factory rather than spelling inline — that file's rule, and the
 * reason `lib/board-face.ts` had to carry an "interim key" apology for a chunk.
 */
export function cardAddressQueryOptions(shortLink: string) {
  return queryOptions({
    queryKey: queryKeys.cards.byShortLink(shortLink),
    queryFn: () => getCardByShortLink(shortLink),
  });
}

export const Route = createFileRoute('/c/$shortLink/{-$slug}')({
  /*
   * Same reasoning as the board route's: this route's terminal state is the
   * board face, and its transient states are the board face's own
   * (`BoardStates.tsx`). A centred reading column here would draw the loading
   * strip at a different width from the board it becomes.
   */
  staticData: { layout: 'full-bleed' },
  component: CardAddressRoute,
});

function CardAddressRoute(): ReactElement {
  const { shortLink } = Route.useParams();
  const matchPathname = Route.useMatch({ select: (match) => match.pathname });
  return <CardAddressView shortLink={shortLink} matchPathname={matchPathname} />;
}

export interface CardAddressViewProps {
  /** The `/c/` segment — a short link or a `KEY-NNNN` reference. */
  readonly shortLink: string;
  /** The matched route's pathname, compared against the canonical form. */
  readonly matchPathname: string;
  /**
   * Set by `/c/$shortLink/com/$number` (`c.$shortLink.com.$number.tsx`). It
   * changes two things only: the canonical address keeps the `/com/<n>` tail
   * (`cardCommentNumberPath`) instead of becoming the card's slug path — which
   * would drop the target with nothing red — and the card back scrolls to and
   * highlights that comment.
   */
  readonly commentNumber?: number | undefined;
}

/**
 * The card address body, shared by `/c/$shortLink/{-$slug}` and
 * `/c/$shortLink/com/$number`.
 */
export function CardAddressView({
  shortLink,
  matchPathname,
  commentNumber,
}: CardAddressViewProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const session = useSession();
  const initialOpenPanel = new URLSearchParams(location.searchStr).get('panel');
  const rawSearch = location.searchStr;
  const rawHash =
    location.hash === '' || location.hash.startsWith('#')
      ? location.hash
      : `#${location.hash}`;
  const address = useQuery(cardAddressQueryOptions(shortLink));
  const [filter, setFilter] = useState<BoardFilterSelection>(() =>
    boardFilterFromSearch(rawSearch),
  );
  const boardId = address.data?.board.id;
  const contents = useQuery({
    ...boardContentsQueryOptions(boardId ?? '', filter),
    enabled: boardId !== undefined,
  });

  const signedIn = session.profile !== null;
  const liveStatus = useBoardLive({
    boardId,
    boardShortLink: address.data?.board.shortLink,
    openCardId: address.data?.id,
    /*
     * The segment this route's resolver query is KEYED by — the URL param, not
     * `address.data.shortLink`. Since `docs/notes/card-keys.md` the param is a
     * `KEY-NNNN` reference on every canonical address, so naming the short link
     * here would invalidate an entry nobody reads, and a key change made by
     * somebody else would never re-canonicalise this tab's address.
     */
    openCardShortLink: address.data === undefined ? undefined : shortLink,
    enabled: signedIn && boardId !== undefined,
  });
  const members = useBoardMembers(boardId ?? '', signedIn && boardId !== undefined);
  const viewer: BoardViewer = {
    signedIn,
    role: ownBoardRole(members.data, session.profile?.id),
    rolesKnown: members.isSuccess && session.profile !== null,
    isSuper: isSuperadminSession(session),
  };

  useEffect(() => {
    setFilter(boardFilterFromSearch(rawSearch));
  }, [rawSearch]);

  const handleFilterChange = useCallback(
    (nextFilter: BoardFilterSelection): void => {
      setFilter(nextFilter);

      const params = withBoardFilterSearchParams(rawSearch, nextFilter);
      const search = params.toString();
      router.history.replace(`${location.pathname}${search === '' ? '' : `?${search}`}`);
      router.history.flush();
    },
    [location.pathname, rawSearch, router.history],
  );

  /*
   * The board's canonical path plus whatever search string the visitor arrived
   * with. Computed in render rather than in the effect so the effect's
   * dependency is the string itself — a fresh object from `useQuery` on every
   * render would re-fire the navigation.
   *
   * ⚠ **`isError` is part of the guard, not just `data === undefined`.**
   * `useQuery` keeps the last successful `data` beside a *new* error, so a
   * refetch that fails after a success leaves `address.data` populated while
   * the fallback branches below are rendering — and without this clause the
   * effect would navigate away from the state it was told to show. It cannot
   * happen today (a success unmounts this component on the next tick, so there
   * is nothing left to refetch), which is exactly why the invariant is written
   * down rather than relied on: the test asserts that a 404 issues no
   * board-shaped request at all.
   */
  const canonicalPath =
    address.data === undefined
      ? undefined
      : commentNumber === undefined
        ? address.data.path
        : cardCommentNumberPath(address.data, commentNumber);
  const canonicalDestination =
    canonicalPath === undefined ||
    address.isError ||
    isCanonicalCardPath(matchPathname, canonicalPath)
      ? undefined
      : `${canonicalPath}${location.searchStr}${rawHash}`;

  useEffect(() => {
    if (canonicalDestination === undefined || address.data === undefined) return;
    /*
     * Canonicalising can change the SEGMENT, not just the slug: a short-link
     * URL, or a stale reference after a move or a key change, becomes the
     * card's current `KEY-NNNN` (`docs/notes/card-keys.md`). The segment is
     * this route's param and its query key, so without this seed the hop
     * would re-resolve the card it has just resolved — a second request and a
     * loading frame. The answer is the same card under its canonical name.
     */
    const canonicalSegment = address.data.ref ?? address.data.shortLink;
    if (canonicalSegment !== shortLink) {
      queryClient.setQueryData(
        cardAddressQueryOptions(canonicalSegment).queryKey,
        address.data,
      );
    }
    void navigate({ href: canonicalDestination, replace: true });
  }, [address.data, canonicalDestination, navigate, queryClient, shortLink]);

  if (address.isError) {
    // The third branch — see the docstring. Not `!profile`, and not skipped.
    if (session.isPending) return <BoardLoading />;

    switch (
      boardFallbackFor({ error: address.error, isSignedOut: session.isSignedOut })
    ) {
      case 'sign-in':
        /*
         * `BoardSignInPrompt` owns the copy and the `?next=` round trip
         * (`FR-PERM-001` AC1, agent H). The return path is the card address the
         * visitor actually pasted, search string included — signing in may be
         * exactly what makes it resolve.
         */
        return (
          <BoardSignInPrompt
            returnPath={`${location.pathname}${location.searchStr}${rawHash}`}
          />
        );
      case 'not-found':
        /*
         * `tone='neutral'`, not `'error'`: the request succeeded and answered
         * "no". `EmptyState`'s own contract draws that line — "nothing found"
         * and "the request failed" must never be drawn the same way — so the
         * retry affordance belongs to the `'error'` branch below and to nothing
         * here. A deleted card does not come back on a second press.
         *
         * ⚠ The copy names **neither cause**. `FR-EXP-015`'s edge case asks for
         * "an explanation that the card no longer exists", but the same 404
         * also covers a card on a board this visitor may not see, and claiming
         * deletion would make the sentence an existence oracle in prose after
         * the server refused to be one on the wire. `RouteNotFound`'s shipped
         * sentence resolves both at once; this is its card-shaped twin.
         */
        return (
          <EmptyState
            tone="neutral"
            size="panel"
            title={t('card.notAvailable.title')}
            body={t('card.notAvailable.body')}
            action={
              /*
               * A signed-in visitor who followed a dead link would otherwise be
               * on a page with no way out: this route has no board behind it to
               * fall back to, unlike every other 404 surface in the product.
               * Plain `Link` styling, matching `RouteNotFound`'s own action.
               */
              <Link
                to="/"
                className="font-medium text-accent underline underline-offset-2"
              >
                {t('card.notAvailable.backToBoards')}
              </Link>
            }
          />
        );
      case 'error':
        /*
         * `BoardError` retries **the query that failed**, which here is the only
         * query this route owns.
         */
        return (
          <BoardError error={address.error} onRetry={() => void address.refetch()} />
        );
    }
  }

  if (contents.isError) {
    return <BoardError error={contents.error} onRetry={() => void contents.refetch()} />;
  }

  if (address.data === undefined || contents.data === undefined) return <BoardLoading />;

  const boardPath = cardBoardPath(address.data);
  const boardPathWithFilter = (
    nextFilter: BoardFilterSelection,
    targetBoardPath = boardPath,
  ): string => {
    const params = withBoardFilterSearchParams(rawSearch, nextFilter);
    params.delete('panel');
    const query = params.toString();
    return `${targetBoardPath}${query === '' ? '' : `?${query}`}`;
  };
  const closeCard = () => {
    const cardOpenReturn = readCardOpenReturn(address.data.path);
    const returnBoardPath = cardOpenReturn?.returnBoardPath ?? boardPath;
    if (cardOpenReturn !== undefined) {
      moveCardReturnToBoard(address.data.path, returnBoardPath);
    }
    void navigate({ href: boardPathWithFilter(filter, returnBoardPath), replace: true });
  };
  const filterByLabel = (labelId: string) => {
    const nextFilter = setBoardFilterLabels(filter, [labelId]);
    const cardOpenReturn = readCardOpenReturn(address.data.path);
    const returnBoardPath = cardOpenReturn?.returnBoardPath ?? boardPath;
    if (cardOpenReturn !== undefined) {
      moveCardReturnToBoard(address.data.path, returnBoardPath);
    }
    void navigate({
      href: boardPathWithFilter(nextFilter, returnBoardPath),
      replace: true,
    });
  };

  /*
   * One provider over the board face AND the card back, so a member draws the
   * same separated label in both. The roster is this board's member list plus
   * every card's members; the shell header, `AccountMenu` and the notification
   * bell live in `__root.tsx` beside the `<Outlet />`, outside it.
   */
  return (
    <AvatarLabelsProvider roster={boardAvatarRoster(members.data, contents.data)}>
      <BoardFace
        contents={contents.data}
        canEdit={canEdit(contents.data.board, viewer)}
        liveStatus={liveStatus}
        onDeleted={() => void navigate({ to: '/', replace: true })}
        filter={filter}
        onFilterChange={handleFilterChange}
      />
      <CardBack
        cardId={address.data.id}
        boardId={address.data.board.id}
        workspaceId={contents.data.board.workspaceId}
        boardClosed={contents.data.board.closed}
        canEdit={canEdit(contents.data.board, viewer)}
        boardMembers={members.data ?? []}
        currentUserId={session.profile?.id}
        currentUserIsSuper={viewer.isSuper}
        initialOpenPanel={initialOpenPanel === 'dates' ? 'dates' : undefined}
        targetCommentNumber={commentNumber}
        onFilterByLabel={filterByLabel}
        onClose={closeCard}
      />
    </AvatarLabelsProvider>
  );
}
